/**
 * Vaste-stap game loop met een accumulator.
 *
 * Simulatie (beweging, AI, collision, missielogica) draait deterministisch op FIXED_STEP;
 * rendering gebeurt zo vaak als het scherm toelaat, met een interpolatie-alpha voor de
 * tussenstand. MAX_CATCHUP voorkomt een death spiral na een tab-switch of een lange stall.
 */

/** Simulatiestap in seconden — 60 Hz. */
export const FIXED_STEP = 1 / 60;

/** Maximaal aantal simulatiestappen dat één frame mag inhalen. */
const MAX_CATCHUP = 5;

/** Bovengrens op de gemeten frametijd, zodat een pauze niet als één enorme delta binnenkomt. */
const MAX_FRAME_TIME = MAX_CATCHUP * FIXED_STEP;

export interface LoopCallbacks {
  /** Deterministische simulatiestap. `dt` is altijd exact FIXED_STEP. */
  update(dt: number): void;
  /** Tekenen. `alpha` is 0..1 tussen de vorige en de huidige simulatiestaat. */
  render(alpha: number): void;
}

export class Loop {
  #running = false;
  #frameId = 0;
  #lastTime = 0;
  #accumulator = 0;

  /** Seconden sinds de loop startte, alleen opgehoogd door simulatiestappen. */
  elapsed = 0;

  constructor(private readonly callbacks: LoopCallbacks) {}

  start(): void {
    if (this.#running) return;
    this.#running = true;
    this.#lastTime = performance.now();
    this.#accumulator = 0;
    this.#frameId = requestAnimationFrame(this.#tick);
  }

  stop(): void {
    if (!this.#running) return;
    this.#running = false;
    cancelAnimationFrame(this.#frameId);
  }

  get running(): boolean {
    return this.#running;
  }

  readonly #tick = (now: number): void => {
    if (!this.#running) return;
    this.#frameId = requestAnimationFrame(this.#tick);

    const frameTime = Math.min((now - this.#lastTime) / 1000, MAX_FRAME_TIME);
    this.#lastTime = now;
    this.#accumulator += frameTime;

    while (this.#accumulator >= FIXED_STEP) {
      this.callbacks.update(FIXED_STEP);
      this.#accumulator -= FIXED_STEP;
      this.elapsed += FIXED_STEP;
    }

    this.callbacks.render(this.#accumulator / FIXED_STEP);
  };
}
